import type { ComponentProps } from 'solid-js'
import { splitProps } from 'solid-js'

import { formatDurationDigits } from '../../utils/units'
import TextInput from './TextInput'

type DurationInputProps = Omit<ComponentProps<'input'>, 'value' | 'onInput'> & {
   value: string
   /** Called with the reformatted duration (`m:ss` / `h:mm:ss`) on every edit. */
   onValueInput: (value: string) => void
}

/**
 * Renders a digits-only duration input that formats as the user types, so
 * `1012` reads `10:12` without a colon key (Android numeric keypads lack one).
 *
 * @param props - Standard input props plus a string `value` and `onValueInput`.
 * @returns The input element.
 */
const DurationInput = (props: DurationInputProps) => {
   const [local, rest] = splitProps(props, ['value', 'onValueInput'])

   /**
    * Reformats the field in place and keeps the caret at the end, where the next
    * digit lands.
    *
    * @param event - The input event.
    */
   const handleInput = (event: InputEvent & { currentTarget: HTMLInputElement }) => {
      if (event.isComposing) {
         return
      }
      const input = event.currentTarget
      const formatted = formatDurationDigits(input.value)
      // Write the DOM directly: a rejected keystroke leaves the signal unchanged,
      // so Solid would not re-render the stray character away.
      input.value = formatted
      input.setSelectionRange(formatted.length, formatted.length)
      local.onValueInput(formatted)
   }

   return (
      <TextInput
         type="text"
         inputmode="numeric"
         autocomplete="off"
         placeholder="0:00"
         {...rest}
         value={local.value}
         onInput={handleInput}
      />
   )
}

export default DurationInput
